/**
 * Auto-rétablissement d'un écran Clock laissé ouvert en permanence.
 *
 * Deux situations laissaient l'écran figé derrière « DONNÉES NON
 * SYNCHRONISÉES » jusqu'à un F5 manuel après une mise à jour :
 *  - la resynchronisation ne revient pas d'elle-même ;
 *  - la page tourne encore l'ancien code face au serveur mis à jour.
 *
 * Dans les deux cas, recharger la page suffit : le secret de l'écran est gardé
 * sur le poste. Ce module décide QUAND recharger ; le hook `useClockRecovery`
 * exécute. Rien ici n'envoie de commande : la Clock reste en lecture seule.
 */

/** Durée d'affichage du bandeau rouge avant le premier rechargement. */
export const CLOCK_STALE_RELOAD_MS = 30_000
/** Plafond entre deux rechargements quand le défaut persiste. */
export const CLOCK_STALE_RELOAD_MAX_MS = 10 * 60_000
/** Synchro saine pendant ce délai : le compteur de tentatives repart à zéro. */
export const CLOCK_HEALTHY_RESET_MS = 60_000
/** Contrôle périodique de la version publiée, en plus de chaque reconnexion. */
export const CLOCK_VERSION_CHECK_MS = 5 * 60_000

export interface ClockRecoveryState {
  /** Rechargements pour désynchronisation depuis la dernière synchro saine. */
  attempts: number
  /** Version pour laquelle on a déjà rechargé : jamais deux fois la même. */
  versionTarget: string | null
}

const EMPTY_STATE: ClockRecoveryState = { attempts: 0, versionTarget: null }

/**
 * Délai de bandeau rouge avant de recharger, doublé à chaque tentative : un
 * défaut que le rechargement ne corrige pas (réseau du poste trop lent) ne
 * doit pas faire clignoter l'écran toutes les 30 s.
 */
export function staleReloadDelayMs(attempts: number): number {
  const safe = Math.max(0, Math.floor(attempts))
  return Math.min(CLOCK_STALE_RELOAD_MAX_MS, CLOCK_STALE_RELOAD_MS * 2 ** Math.min(safe, 16))
}

export function staleReloadDue(staleForMs: number | null, attempts: number): boolean {
  return staleForMs != null && staleForMs >= staleReloadDelayMs(attempts)
}

/**
 * Signature de la version publiée : le chemin du bundle principal, dont le nom
 * porte l'empreinte du build (`/assets/index-XXXX.js`). Elle change à chaque
 * release du frontend, et à elle seule.
 */
export function bundleSignatureFromHtml(html: string, origin: string): string | null {
  for (const match of html.matchAll(/<script\b[^>]*>/gi)) {
    const tag = match[0]
    if (!/\btype\s*=\s*["']module["']/i.test(tag)) continue
    const src = /\bsrc\s*=\s*["']([^"']+)["']/i.exec(tag)?.[1]
    if (src) return normalizeSrc(src, origin)
  }
  return null
}

export function bundleSignatureFromDocument(doc: Document, origin: string): string | null {
  const src = doc.querySelector('script[type="module"][src]')?.getAttribute('src')
  return src ? normalizeSrc(src, origin) : null
}

function normalizeSrc(src: string, origin: string): string | null {
  try { return new URL(src, origin).pathname } catch { return null }
}

/**
 * Recharger pour une nouvelle version ? Oui si le serveur publie un autre
 * bundle que celui en mémoire — une seule fois par version, pour qu'un cache
 * récalcitrant ne déclenche pas une boucle de rechargements.
 */
export function versionReloadDue(current: string | null, published: string | null, state: ClockRecoveryState): boolean {
  return Boolean(current && published && current !== published && state.versionTarget !== published)
}

export function parseRecoveryState(raw: string | null): ClockRecoveryState {
  if (!raw) return { ...EMPTY_STATE }
  try {
    const value = JSON.parse(raw) as Partial<ClockRecoveryState>
    return {
      attempts: typeof value.attempts === 'number' && Number.isFinite(value.attempts) ? Math.max(0, Math.floor(value.attempts)) : 0,
      versionTarget: typeof value.versionTarget === 'string' ? value.versionTarget : null,
    }
  } catch {
    return { ...EMPTY_STATE }
  }
}
